import React from 'react';
import './PerformanceChart.css';

const PerformanceChart = ({ applications = [], users = [] }) => {
  if (!applications.length || !users.length) {
    return (
      <div className="performance-chart">
        <div className="performance-empty">
          <p>Нет данных для отображения производительности</p>
        </div>
      </div>
    );
  }

  // Считаем статистику по каждому пользователю
  const userStats = users
    .filter(u => u.role === 'admin' || u.role === 'superadmin')
    .map(user => {
      const userApps = applications.filter(a => {
        const assignedId = a.assignedTo?._id || a.assignedTo;
        return String(assignedId) === String(user._id);
      });

      const total = userApps.length;
      const completed = userApps.filter(a => a.status === 'completed').length;
      const inProgress = userApps.filter(a => a.status === 'in_progress').length;
      const overdue = userApps.filter(a => a.isOverdue).length;
      const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

      // Среднее время выполнения
      const completedApps = userApps.filter(a => a.status === 'completed' && a.completedAt);
      const avgTime = completedApps.length > 0
        ? Math.round(completedApps.reduce((sum, a) => {
            const time = (new Date(a.completedAt) - new Date(a.createdAt)) / (1000 * 60 * 60);
            return sum + time;
          }, 0) / completedApps.length)
        : null;

      return { user, total, completed, inProgress, overdue, completionRate, avgTime };
    })
    .filter(s => s.total > 0)
    .sort((a, b) => b.completionRate - a.completionRate);

  if (userStats.length === 0) {
    return (
      <div className="performance-chart">
        <div className="performance-empty">
          <p>Нет назначенных заявок для отображения</p>
        </div>
      </div>
    );
  }

  const maxTotal = Math.max(...userStats.map(s => s.total), 1);

  return (
    <div className="performance-chart">
      <h3>📊 Производительность сотрудников</h3>

      {/* Сводка */}
      <div className="performance-summary">
        <div className="perf-stat">
          <span className="perf-value">{userStats.length}</span>
          <span className="perf-label">Сотрудников</span>
        </div>
        <div className="perf-stat">
          <span className="perf-value">{userStats.reduce((s, u) => s + u.total, 0)}</span>
          <span className="perf-label">Всего заявок</span>
        </div>
        <div className="perf-stat">
          <span className="perf-value">{userStats.reduce((s, u) => s + u.completed, 0)}</span>
          <span className="perf-label">Выполнено</span>
        </div>
        <div className="perf-stat">
          <span className="perf-value">{userStats.reduce((s, u) => s + u.overdue, 0)}</span>
          <span className="perf-label">Просрочено</span>
        </div>
      </div>

      {/* Таблица производительности */}
      <div className="performance-table">
        <div className="perf-table-header">
          <span className="col-employee">Сотрудник</span>
          <span className="col-total">Заявки</span>
          <span className="col-bar">Загрузка</span>
          <span className="col-rate">Выполнение</span>
          <span className="col-avgtime">Ср. время</span>
          <span className="col-status">Статус</span>
        </div>
        {userStats.map((s, i) => (
          <div key={s.user._id} className="perf-table-row">
            <span className="col-employee">
              <span className="employee-name">{s.user.name}</span>
              <span className="employee-email">{s.user.email}</span>
            </span>
            <span className="col-total">
              <strong>{s.total}</strong>
              <span className="sub-count">(вып. {s.completed})</span>
            </span>
            <span className="col-bar">
              <div className="load-bar-bg">
                <div
                  className="load-bar-fill"
                  style={{
                    width: `${(s.total / maxTotal) * 100}%`,
                    background: s.overdue > 0
                      ? 'linear-gradient(to right, #ef4444, #f59e0b)'
                      : 'linear-gradient(to right, #22c55e, #3b82f6)'
                  }}
                ></div>
              </div>
            </span>
            <span className="col-rate">
              <span className={`rate-badge ${s.completionRate >= 80 ? 'high' : s.completionRate >= 50 ? 'mid' : 'low'}`}>
                {s.completionRate}%
              </span>
            </span>
            <span className="col-avgtime">
              {s.avgTime !== null ? `${s.avgTime} ч.` : '—'}
            </span>
            <span className="col-status">
              <span className={`work-status ${s.user.workStatus || 'working'}`}>
                {(s.user.workStatus || 'working') === 'working' ? '🟢' : '🔴'}
              </span>
            </span>
          </div>
        ))}
      </div>

      {/* Легенда */}
      <div className="performance-legend">
        <span><span className="legend-dot" style={{ background: '#22c55e' }}></span> Отлично (&ge;80%)</span>
        <span><span className="legend-dot" style={{ background: '#f59e0b' }}></span> Средне (50-79%)</span>
        <span><span className="legend-dot" style={{ background: '#ef4444' }}></span> Низко ({'<'}50%)</span>
      </div>
    </div>
  );
};

export default PerformanceChart;